import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuCheck, LuSend } from "react-icons/lu";
import * as styled from "../AddStudent/AddStudent.styled.jsx";
import axios from "../../../axios";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle.jsx";
import Loader from "../../../components/Loader/Loader.jsx";
import { PagesWrapper } from "../../../styles/components/PagesWrapper.styled";
import { useAdmin } from "../../../context/AdminInfo.js";

// Форма «Запит на пробний»: без вчителя і дати — те, що людина відповіла Лері в чаті.
// Запит іде вчителям у бот (@speaksy_teachers_bot), вони відгукуються, а вчителя й час Лера обирає в «Пробних уроках» → «Призначити».
const GROUPS = [
  { name: "Діти 6-12", value: "kids" },
  { name: "Підлітки 13-17", value: "teenagers" },
  { name: "Дорослі", value: "adults" },
];

const LANGS = [
  { name: "Англійська", value: "en" },
  { name: "Німецька", value: "de" },
];

const EMPTY = {
  lang: "",
  name: "",
  age: "",
  phoneNumber: "",
  ageGroup: "",
  level: "",
  trialWhen: "",
  lessonsPerWeek: "",
  regularSchedule: "",
  teacherComment: "",
  notes: "",
};

// Вік → група, щоб не клацати двічі (Лера може змінити)
const groupByAge = (age) => {
  const n = Number(String(age).trim());
  if (!Number.isFinite(n) || n <= 0) return "";
  return n <= 12 ? "kids" : n <= 17 ? "teenagers" : "adults";
};

function TrialRequest() {
  const { adminInfo } = useAdmin();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | saving | done | error
  const [result, setResult] = useState(null);   // { sentTo, botEnabled }
  const [serverError, setServerError] = useState("");

  const set = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "age" && (!prev.ageGroup || prev.ageGroup === groupByAge(prev.age))) next.ageGroup = groupByAge(value);
      return next;
    });
    setErrors((prev) => ({ ...prev, [key]: undefined, ...(key === "age" ? { ageGroup: undefined } : {}) }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Вкажіть імʼя";
    const telegramNick = /^(@|t\.me\/|https:\/\/t\.me\/)[a-z0-9_]{4,}$/i.test(form.phoneNumber.trim());
    if (form.phoneNumber.replace(/\D/g, "").length < 5 && !telegramNick) next.phoneNumber = "Телефон або @нік — за ним бот видасть доступ";
    if (!form.ageGroup) next.ageGroup = "Оберіть групу";
    if (!form.trialWhen.trim()) next.trialWhen = "Коли зручно на пробний?";
    if (adminInfo.accessType === "all" && !form.lang) next.lang = "Оберіть мову";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setStatus("saving");
    setServerError("");
    try {
      const { lang, ...fields } = form;
      const { data } = await axios.post("trial-requests", { ...fields, accessType: adminInfo.accessType === "all" ? lang : adminInfo.accessType });
      setResult(data);
      setStatus("done");
    } catch (error) {
      setServerError(error?.response?.data?.message || "");
      setStatus("error");
    }
  };

  if (!adminInfo) return <Loader />;

  if (status === "done") {
    return (
      <PagesWrapper>
        <styled.Done>
          <styled.DoneIcon>
            <LuCheck size={32} />
          </styled.DoneIcon>
          <h2>Запит на пробний створено</h2>
          {!result?.botEnabled ? (
            <p>Бот вчителів вимкнений — запит видно в «Пробних уроках», вчителя оберіть там.</p>
          ) : result.sentTo ? (
            <p>Надіслали вчителям у бот: {result.sentTo}. Відгуки зʼявляться в «Пробних уроках» і в чаті менеджерів.</p>
          ) : (
            <p>Ще жоден вчитель не підключив бота — запит видно в «Пробних уроках», вчителя оберіть там.</p>
          )}
          <styled.Actions>
            <styled.Secondary type="button" onClick={() => navigate("/admin/trial-lessons")}>
              До пробних уроків
            </styled.Secondary>
            <styled.Primary type="button" onClick={() => { setForm(EMPTY); setErrors({}); setResult(null); setStatus("idle"); }}>
              Ще один запит
            </styled.Primary>
          </styled.Actions>
        </styled.Done>
      </PagesWrapper>
    );
  }

  const text = (key, label, { optional, ...props } = {}) => (
    <styled.Field>
      <styled.Label>
        {label}
        {optional && <styled.Optional>необовʼязково</styled.Optional>}
      </styled.Label>
      <styled.Input name={key} value={form[key]} $error={!!errors[key]} onChange={(event) => set(key, event.target.value)} {...props} />
      {errors[key] && <styled.Error>{errors[key]}</styled.Error>}
    </styled.Field>
  );

  return (
    <PagesWrapper>
      <TopPageTitle value="Запит на пробний" />
      <styled.Form onSubmit={onSubmit} noValidate>
        <styled.Section>
          <styled.SectionTitle>
            <span>1</span> Хто
          </styled.SectionTitle>
          {adminInfo.accessType === "all" && (
            <styled.Field>
              <styled.Label>Мова (запит піде вчителям цієї мови)</styled.Label>
              <styled.Chips>
                {LANGS.map(({ name, value }) => (
                  <styled.Chip key={value} type="button" $active={form.lang === value} onClick={() => set("lang", value)}>
                    {name}
                  </styled.Chip>
                ))}
              </styled.Chips>
              {errors.lang && <styled.Error>{errors.lang}</styled.Error>}
            </styled.Field>
          )}
          <styled.Grid>
            {text("name", "Імʼя учня", { placeholder: "Лера" })}
            {text("age", "Вік", { placeholder: "14", inputMode: "numeric", optional: true })}
          </styled.Grid>
          {text("phoneNumber", "Телефон", { placeholder: "+380… або @нік у Telegram" })}
          <styled.Field>
            <styled.Label>Вікова група</styled.Label>
            <styled.Chips>
              {GROUPS.map(({ name, value }) => (
                <styled.Chip key={value} type="button" $active={form.ageGroup === value} onClick={() => set("ageGroup", value)}>
                  {name}
                </styled.Chip>
              ))}
            </styled.Chips>
            {errors.ageGroup && <styled.Error>{errors.ageGroup}</styled.Error>}
          </styled.Field>
        </styled.Section>

        <styled.Section>
          <styled.SectionTitle>
            <span>2</span> Відповіді на питання
          </styled.SectionTitle>
          {text("trialWhen", "Коли зручно на пробний", { placeholder: "пʼятниця 11:00–12:00" })}
          <styled.Grid>
            {text("lessonsPerWeek", "Скільки разів на тиждень потім", { placeholder: "2 рази / поки не визначились", optional: true })}
            {text("level", "Рівень зі слів клієнта", { placeholder: "A1, може A2", optional: true })}
          </styled.Grid>
          {text("regularSchedule", "Дні й час для регулярних", { placeholder: "пн, ср, пт після 16:30", optional: true })}
        </styled.Section>

        <styled.Section>
          <styled.SectionTitle>
            <span>3</span> Коментарі
          </styled.SectionTitle>
          {text("teacherComment", "Для вчителів", { placeholder: "напр. готується до НМТ", optional: true })}
          {text("notes", "Лише для нас", { placeholder: "звідки прийшов, домовленості…", optional: true })}
          <styled.Placeholder>У боті вчителі побачать імʼя, вік, групу, відповіді й коментар для вчителів. Телефону й нотаток «лише для нас» у боті немає.</styled.Placeholder>
        </styled.Section>

        {Object.values(errors).some(Boolean) && <styled.Alert>Перевірте поля, позначені червоним</styled.Alert>}
        {status === "error" && <styled.Alert>{serverError || "Не вдалося створити запит. Спробуйте ще раз."}</styled.Alert>}

        <styled.Primary type="submit" disabled={status === "saving"}>
          <LuSend size={20} />
          {status === "saving" ? "Надсилаємо…" : "Надіслати вчителям"}
        </styled.Primary>
      </styled.Form>
    </PagesWrapper>
  );
}

export default TrialRequest;
